import { NavIcons } from '@pipebase/ui';
import { findActiveNavItem as findActiveNavItemBase, type NavGroup, type NavItem } from '@pipebase/ui';

export type { NavItem, NavGroup };

/** A nav item, plus `cloudHidden`: left out on a Pipebase Cloud designer,
 *  whose backend refuses the routes behind it (src/cloud.tsx). */
export type SpaNavItem = NavItem & { cloudHidden?: true };
export type SpaNavGroup = Omit<NavGroup, 'items'> & { items: SpaNavItem[] };

/**
 * Sidebar information architecture from the design handoff
 * (ipaas/project/platform-shell.jsx). Every item maps to a real
 * page now — the IA matches the design's full surface.
 */
export const SPA_NAV_GROUPS: SpaNavGroup[] = [
  {
    id: 'operate',
    title: 'Operate',
    items: [
      { id: 'home',   label: 'Dashboard',     path: '/',         icon: NavIcons.home },
      { id: 'runs',   label: 'Runs',          path: '/monitor',  icon: NavIcons.runs },
      { id: 'queues', label: 'Message Queues', path: '/queues',  icon: NavIcons.queue },
      { id: 'trace',  label: 'Live Trace',    path: '/trace',    icon: NavIcons.trace, feature: 'trace' },
      { id: 'logs',   label: 'Logs',          path: '/logs',     icon: NavIcons.log,   feature: 'logs' },
    ],
  },
  {
    id: 'build',
    title: 'Build',
    items: [
      { id: 'flows',          label: 'Integration Flows', path: '/flows',          icon: NavIcons.flows },
      { id: 'mappings',       label: 'Mappings',          path: '/mappings',       icon: NavIcons.api },
      // Event Designs sit between Mappings and Value Mappings per the
      // foundation PRD — peer of Flow / Mapping / Value Mapping.
      { id: 'event-designs',  label: 'Event Designs',     path: '/event-designs',  icon: NavIcons.conn, cloudHidden: true },
      { id: 'value-mappings', label: 'Value Mappings',    path: '/value-mappings', icon: NavIcons.schema },
    ],
  },
  {
    id: 'connect',
    title: 'Connect',
    items: [
      { id: 'connections', label: 'Connections',  path: '/connections',  icon: NavIcons.conn },
      { id: 'mesh',        label: 'Mesh',         path: '/mesh',         icon: NavIcons.conn,   feature: 'mesh', cloudHidden: true },
      { id: 'schemas',     label: 'Schemas',      path: '/schemas',      icon: NavIcons.schema, feature: 'schemas', cloudHidden: true },
      { id: 'credentials', label: 'OAuth Clients', path: '/oauth-clients', icon: NavIcons.cred },
      { id: 'variables',   label: 'Parameters',  path: '/parameters',   icon: NavIcons.vars },
    ],
  },
  {
    id: 'govern',
    title: 'Govern',
    items: [
      { id: 'cutover',       label: 'Cutover',       path: '/cutover',       icon: NavIcons.vars },
      { id: 'audit',         label: 'Audit Log',     path: '/audit',         icon: NavIcons.audit, feature: 'audit' },
      { id: 'event-catalog', label: 'Event Catalog', path: '/event-catalog', icon: NavIcons.conn, cloudHidden: true },
      { id: 'policies',      label: 'Policies',      path: '/policies',      icon: NavIcons.policy },
      { id: 'access',        label: 'Roles',         path: '/roles',         icon: NavIcons.access },
    ],
  },
  {
    id: 'extend',
    title: 'Extend',
    items: [
      // Marketplace re-added with the static template catalogue from the
      // design. v1 forks via api.createFlow with the template name; the
      // proper template manifest comes when the marketplace API lands.
      { id: 'market',   label: 'Marketplace',     path: '/marketplace',  icon: NavIcons.market, feature: 'marketplace', cloudHidden: true },
      { id: 'webhooks', label: 'API & Webhooks',  path: '/api-webhooks', icon: NavIcons.api,    feature: 'apiWebhooks' },
    ],
  },
  {
    id: 'org',
    title: 'Organization',
    items: [
      { id: 'settings', label: 'Settings', path: '/settings', icon: NavIcons.settings },
      { id: 'envs',     label: 'Runtimes', path: '/runtimes', icon: NavIcons.env },
    ],
  },
];

/** The nav for this designer: on Pipebase Cloud without the `cloudHidden`
 *  items (and any group they leave empty). */
export function navGroupsFor(cloud: boolean): SpaNavGroup[] {
  if (!cloud) return SPA_NAV_GROUPS;
  return SPA_NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => !i.cloudHidden) })).filter(
    (g) => g.items.length > 0,
  );
}

/**
 * Stub destinations live under /not-implemented/<id>. Kept for backward
 * compatibility with old links — the new IA wires every item to a real route.
 */
export function stubPath(id: string): string {
  return `/not-implemented/${id}`;
}

export function navItemPath(item: NavItem): string {
  return item.path ?? stubPath(item.id);
}

/** Find the nav item that owns the given pathname. Used for breadcrumbs +
 *  sidebar active state. Falls back to nearest path-prefix match. Also
 *  handles stub routes (/not-implemented/<id>). */
export function findActiveNavItem(pathname: string): NavItem | undefined {
  // Delegate to the package helper for exact + prefix matching.
  const match = findActiveNavItemBase(SPA_NAV_GROUPS, pathname);
  if (match) return match;
  // Stub route (SPA-specific — /not-implemented/<id>).
  const allItems = SPA_NAV_GROUPS.flatMap((g) => g.items);
  const stubMatch = pathname.match(/^\/not-implemented\/([^/]+)/);
  if (stubMatch) return allItems.find((i) => i.id === stubMatch[1]);
  return undefined;
}

/** Editor routes live outside the regular IA. Used by the shell to decide
 *  whether to render the chrome at all. */
export function isEditorRoute(pathname: string): boolean {
  return (
    pathname.startsWith('/edit/') ||
    pathname.startsWith('/edit-next/') ||
    // Event Design's own editor — different entity, different canvas, but
    // same chrome-hiding contract.
    pathname.startsWith('/event-design/') ||
    // Onboarding wizard hosts its own minimal chrome.
    pathname === '/onboarding' ||
    pathname.startsWith('/onboarding/')
  );
}
